import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import suppliersService from '../services/suppliersService';
import TagsInput from './TagsInput';
import { useNotification } from '../context/NotificationContext';

const RFC_PATTERN = /^([A-ZÑ&]{3,4})(\d{2})(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])([A-Z\d]{3})$/;

function validateRFC(value) {
  if (!value || !value.trim()) return null;
  return RFC_PATTERN.test(value.trim().toUpperCase());
}

/**
 * SupplierForm
 * Reusable supplier create/edit form, structural mirror of ClientForm.jsx.
 */
const SupplierForm = ({ initialData = null, onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const isEditMode = !!initialData?.id;

  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    rfc: initialData?.rfc || '',
    phones: initialData?.phones || [],
    products: initialData?.products || [],
    account_number: initialData?.account_number || '',
    bank: initialData?.bank || '',
    has_credit: initialData?.has_credit || false,
    is_active: initialData?.is_active ?? true
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = t('suppliers.nameRequired');
    if (formData.rfc.trim() && !validateRFC(formData.rfc)) {
      newErrors.rfc = t('suppliers.invalidRFC');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRfcBlur = () => {
    const cleaned = formData.rfc.trim().toUpperCase();
    setFormData((prev) => ({ ...prev, rfc: cleaned }));
    if (cleaned && !validateRFC(cleaned)) {
      setErrors((prev) => ({ ...prev, rfc: t('suppliers.invalidRFC') }));
    } else {
      setErrors((prev) => ({ ...prev, rfc: undefined }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSaving(true);
    try {
      let supplier;
      if (isEditMode) {
        const res = await suppliersService.updateSupplier(initialData.id, formData);
        supplier = res.data;
        notify.success(t('suppliers.supplierUpdated'));
      } else {
        const res = await suppliersService.createSupplier(formData);
        supplier = res.data;
        notify.success(t('suppliers.supplierCreated'));
      }
      onSuccess(supplier);
    } catch (err) {
      const errMsg = err.response?.data?.detail || (isEditMode ? t('common.failedToUpdate') : t('common.failedToCreate'));
      notify.error(errMsg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <fieldset className="form-fieldset">
        <legend>{t('suppliers.title')}</legend>

        <div className="form-group">
          <label className="form-label required">{t('suppliers.name')}</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder={t('suppliers.name')}
            className={`form-input ${errors.name ? 'error' : ''}`}
          />
          {errors.name && <span className="form-error">{errors.name}</span>}
        </div>

        <div className="form-group">
          <label className="form-label">{t('suppliers.rfc')}</label>
          <input
            type="text"
            name="rfc"
            value={formData.rfc}
            onChange={handleChange}
            onBlur={handleRfcBlur}
            placeholder={t('suppliers.rfc')}
            className={`form-input ${errors.rfc ? 'error' : ''}`}
          />
          {errors.rfc && <span className="form-error">{errors.rfc}</span>}
        </div>

        <div className="grid-2">
          <div className="form-group">
            <label className="form-label">{t('suppliers.accountNumber')}</label>
            <input
              type="text"
              name="account_number"
              value={formData.account_number}
              onChange={handleChange}
              placeholder={t('suppliers.accountNumber')}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('suppliers.bank')}</label>
            <input
              type="text"
              name="bank"
              value={formData.bank}
              onChange={handleChange}
              placeholder={t('suppliers.bank')}
              className="form-input"
            />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">
            <input
              type="checkbox"
              name="has_credit"
              checked={formData.has_credit}
              onChange={handleCheckboxChange}
            />
            {' '}{t('suppliers.hasCredit')}
          </label>
        </div>

        <div className="form-group">
          <label className="form-label">
            <input
              type="checkbox"
              name="is_active"
              checked={formData.is_active}
              onChange={handleCheckboxChange}
            />
            {' '}{t('suppliers.active')}
          </label>
        </div>
      </fieldset>

      <fieldset className="form-fieldset">
        <legend>{t('suppliers.products')}</legend>
        <TagsInput
          value={formData.products}
          onChange={(next) => setFormData((prev) => ({ ...prev, products: next }))}
          placeholder={t('suppliers.tagsInputPlaceholder')}
        />
      </fieldset>

      <fieldset className="form-fieldset">
        <legend>{t('suppliers.phones')}</legend>
        <TagsInput
          value={formData.phones}
          onChange={(next) => setFormData((prev) => ({ ...prev, phones: next }))}
          placeholder={t('suppliers.tagsInputPlaceholder')}
        />
      </fieldset>

      <div className="form-actions">
        <button type="submit" disabled={saving} className="btn btn-primary">
          {saving ? t('common.loading') : t('common.save')}
        </button>
        <button type="button" onClick={onCancel} className="btn btn-secondary">
          {t('common.cancel')}
        </button>
      </div>
    </form>
  );
};

export default SupplierForm;
